/**
 * Shared primitives for the super-admin pages: row types the admin API
 * returns, number formatters, and the small print-styled building blocks
 * (stat tile, table, section header, sub-tab strip) every page composes.
 *
 * Kept deliberately local to `pages/admin` — these are operator-only
 * surfaces and none of this should leak into customer-facing chrome.
 */

import { Link } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { ResponsiveContainer } from "recharts";
import { Card } from "../../ui/card.js";

/* ─── Row types ─────────────────────────────────────────────── */

export interface RecentTurn {
  id: string;
  workspace_id: string;
  workspace_name: string | null;
  agent_id: string;
  channel_id: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  cost_millicents: number;
  duration_ms: number;
  created_at: string;
}

export interface Checkpoint {
  turn_id: string;
  channel_id: string;
  agent_id: string;
  customer_id: string;
  node: string;
  created_at: string;
  updated_at: string;
}

export interface ToolExecution {
  tool_call_id: string;
  turn_id: string;
  tool_name: string;
  status: string;
  started_at: string;
  completed_at: string | null;
}

export interface WorkspaceRow {
  id: string;
  slug: string;
  name: string;
  customer_id: string;
  plan: string;
  status: string;
  daily_tokens_used: string;
  daily_token_ceiling: string;
  daily_credits_used_millicents: string;
  daily_credit_ceiling_millicents: string;
  created_at: string;
  member_count: string;
  channel_count: string;
  tokens_30d: string;
  cost_30d_millicents: string;
  credits_granted?: string | number;
  /** Credits consumed in the trailing 30 days (sell side, from the ledger). */
  credits_used_30d?: string | number;
  /** Has ever received an `internal` grant: the dogfood and staff rows. */
  is_internal?: boolean;
  last_activity_at: string | null;
  first_user_email: string | null;
}

export type EditionSlot = "morning" | "midday" | "evening";

export interface EditionPublishSchedule {
  id: string;
  workspace_id: string;
  slot: EditionSlot;
  enabled: boolean;
  publish_time: string;
  weekdays_only: boolean;
}

export interface WorkspaceUserRow {
  id: string;
  email: string;
  display_name: string;
  email_verified: boolean;
  user_created_at: string;
  role: string;
  joined_at: string;
  last_session_at: string | null;
}

export interface DailyUsage {
  day: string;
  turn_count: string;
  prompt_tokens: string;
  completion_tokens: string;
  cost_millicents: string;
  active_workspaces: string;
}

export interface ConnectedServiceRow {
  service_type: string;
  count: string;
  workspace_count: string;
}

export interface FeedbackRow {
  id: string;
  conversation_id: string;
  customer_id: string;
  workspace_id: string;
  user_id: string;
  status: "new" | "triaged" | "resolved";
  internal_notes: string | null;
  debug_context: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  workspace_name: string | null;
  workspace_slug: string | null;
  user_display_name: string | null;
  user_email: string | null;
  first_message: string | null;
  message_count: number;
}

/* ─── Formatters ────────────────────────────────────────────── */

export function formatCost(millicents: number): string {
  return `$${(millicents / 100_000).toFixed(4)}`;
}

/** Whole-dollar-ish cost for tiles where four decimals would be noise. */
export function formatCostShort(millicents: number): string {
  const dollars = millicents / 100_000;
  if (dollars >= 100) return `$${Math.round(dollars).toLocaleString("en-US")}`;
  return `$${dollars.toFixed(2)}`;
}

export function formatCredits(credits: number): string {
  return credits.toFixed(2);
}

/** Compact credits for leaderboards and tiles: 1,118 rather than 1118.42. */
export function formatCreditsCompact(credits: number): string {
  if (Math.abs(credits) >= 100) return Math.round(credits).toLocaleString("en-US");
  return credits.toFixed(credits === 0 ? 0 : 1);
}

export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function timeAgo(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function formatDayTick(day: string): string {
  // "2026-04-17" -> "Apr 17"
  const d = new Date(`${day}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Signed percentage change, or a word when there is no prior baseline. */
export function formatDelta(current: number, prior: number): { label: string; tone: "up" | "down" | "flat" } {
  if (prior <= 0) return current > 0 ? { label: "new", tone: "up" } : { label: "—", tone: "flat" };
  const pct = ((current - prior) / prior) * 100;
  if (Math.abs(pct) < 0.5) return { label: "±0%", tone: "flat" };
  const rounded = Math.round(pct);
  return { label: `${rounded > 0 ? "+" : "−"}${Math.abs(rounded)}%`, tone: rounded > 0 ? "up" : "down" };
}

/* ─── Building blocks ───────────────────────────────────────── */

export function StatCard({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  /** Colors the `sub` line: a delta that went the good way, or the bad way. */
  tone?: "up" | "down" | "flat";
}) {
  const subColor =
    tone === "up" ? "var(--success)" : tone === "down" ? "var(--error)" : "var(--text-secondary)";
  return (
    <div
      className="rounded-[var(--radius-card)] border p-4 flex items-start gap-3"
      style={{ borderColor: "var(--print-border)", background: "var(--print-bg)", boxShadow: "var(--shadow-print)" }}
    >
      <div className="p-2 rounded-lg" style={{ background: "var(--accent-muted)" }}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "var(--text-label)", fontFamily: "var(--font-mono)" }}>
          {label}
        </div>
        <div className="text-xl font-semibold mt-0.5 tabular-nums" style={{ color: "var(--text-primary)" }}>
          {value}
        </div>
        {sub && (
          <div className="text-[12px] mt-0.5" style={{ color: subColor, fontFamily: tone ? "var(--font-mono)" : undefined }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

export function Table({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border" style={{ borderColor: "var(--print-border)", background: "var(--print-bg)", boxShadow: "var(--shadow-print)" }}>
      <table className="w-full text-[13px]" style={{ color: "var(--text-primary)" }}>
        {children}
      </table>
    </div>
  );
}

export function Th({ children, align }: { children: React.ReactNode; align?: "right" }) {
  return (
    <th
      className={`${align === "right" ? "text-right" : "text-left"} px-3 py-2 text-[11px] uppercase tracking-wider font-medium border-b`}
      style={{ color: "var(--text-label)", borderColor: "var(--border-subtle)", background: "rgba(var(--ink-rgb), 0.04)", fontFamily: "var(--font-mono)" }}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  mono,
  colSpan,
  align,
}: {
  children: React.ReactNode;
  mono?: boolean;
  colSpan?: number;
  align?: "right";
}) {
  return (
    <td
      colSpan={colSpan}
      className={`px-3 py-2 border-b ${mono ? "font-mono text-[12px]" : ""} ${align === "right" ? "text-right tabular-nums" : ""}`}
      style={{ borderColor: "var(--border-subtle)" }}
    >
      {children}
    </td>
  );
}

export function SectionHeader({ title, icon, action }: { title: string; icon: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h2 className="text-sm font-semibold" style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>
        {title}
      </h2>
      {action && <span className="ml-auto">{action}</span>}
    </div>
  );
}

export function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={24} className="animate-spin" style={{ color: "var(--text-tertiary)" }} />
    </div>
  );
}

export function ChartCard({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>
              {title}
            </div>
            {subtitle && (
              <div className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                {subtitle}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="h-64 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          {/* recharts requires a single child element for ResponsiveContainer */}
          {children as React.ReactElement}
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/**
 * Link-based sub-tab strip. Tabs are real URLs (the browser back button and
 * a pasted link both land on the right tab), so this is a `<nav>` of Links,
 * not buttons with local state.
 */
export function SubTabs({
  tabs,
  active,
  label,
}: {
  tabs: Array<{ key: string; label: string; to: string; badge?: number }>;
  active: string;
  label: string;
}) {
  return (
    <nav
      aria-label={label}
      className="flex gap-1 border-b overflow-x-auto"
      style={{ borderColor: "var(--border)" }}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <Link
            key={tab.key}
            to={tab.to}
            aria-current={isActive ? "page" : undefined}
            className="px-2.5 py-2 text-[12.5px] whitespace-nowrap no-underline -mb-px border-b-2"
            style={{
              color: isActive ? "var(--accent)" : "var(--text-secondary)",
              borderColor: isActive ? "var(--accent)" : "transparent",
              fontWeight: isActive ? 600 : 500,
            }}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span
                className="ml-1.5 px-1.5 rounded text-[10px] font-mono"
                style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
              >
                {tab.badge}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

export type StatsWindow = 7 | 30 | 90;

export function WindowToggle({ value, onChange }: { value: StatsWindow; onChange: (v: StatsWindow) => void }) {
  const options: StatsWindow[] = [7, 30, 90];
  return (
    <div
      className="inline-flex rounded-md border p-0.5"
      style={{ borderColor: "var(--border)", background: "rgba(var(--glass-rgb), 0.55)" }}
      role="tablist"
      aria-label="Time window"
    >
      {options.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt)}
            className="px-3 py-1 text-[12px] font-medium rounded-[4px] transition-colors"
            style={{
              background: active ? "var(--accent-muted)" : "transparent",
              color: active ? "var(--accent)" : "var(--text-secondary)",
            }}
          >
            {opt} days
          </button>
        );
      })}
    </div>
  );
}

/** Where a workspace's detail lives; every admin surface links here the same way. */
export function workspacePath(workspaceId: string, tab: string = "usage"): string {
  return `/admin/workspaces/${encodeURIComponent(workspaceId)}/${tab}`;
}

/** One count on the attention strip: a link to where it resolves, quiet at zero. */
export function AttentionChip({
  count,
  label,
  to,
  warn,
}: {
  count: number;
  label: string;
  to: string;
  warn?: boolean;
}) {
  const quiet = count === 0;
  const badgeBg = quiet ? "rgba(var(--ink-rgb), 0.06)" : warn ? "var(--honesty-bg)" : "var(--accent-muted)";
  const badgeColor = quiet ? "var(--text-tertiary)" : warn ? "var(--honesty)" : "var(--accent)";
  const body = (
    <>
      <span
        className="min-w-[22px] text-center px-1.5 rounded text-[12px] font-semibold tabular-nums"
        style={{ background: badgeBg, color: badgeColor, fontFamily: "var(--font-mono)" }}
      >
        {count}
      </span>
      {!quiet && warn && <AlertTriangle size={12} style={{ color: "var(--honesty)" }} />}
      <span>{label}</span>
    </>
  );
  const className = "inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[12.5px] no-underline";
  const style = {
    borderColor: quiet ? "var(--border-subtle)" : warn ? "var(--honesty-border)" : "var(--border)",
    background: "var(--print-bg)",
    color: quiet ? "var(--text-tertiary)" : "var(--text-primary)",
  };
  if (quiet) {
    return (
      <span className={className} style={style}>
        {body}
      </span>
    );
  }
  return (
    <Link to={to} className={className} style={style}>
      {body}
    </Link>
  );
}
